// utils/fetchData.js

const API_JSON_URL = "/api.json";
const REQUEST_TIMEOUT_MS = 5_000;

let activeRequest = null;
let currentData = null;

/**
 * Check whether browser APIs are available.
 */
const isBrowser = () => typeof window !== "undefined";

/**
 * Check whether an error was caused by cancellation or timeout.
 */
const isAbortError = (error) =>
  error?.name === "AbortError" ||
  error?.name === "TimeoutError" ||
  error?.code === "ABORT_ERR";

/**
 * Support either:
 *
 * {
 *   "data": [...]
 * }
 *
 * or direct JSON:
 *
 * [...]
 */
const extractData = (result) => {
  if (result === null || result === undefined) {
    throw new Error("api.json does not contain usable data.");
  }

  if (typeof result !== "object") {
    throw new Error("api.json must contain valid JSON data.");
  }

  const data = Object.prototype.hasOwnProperty.call(result, "data")
    ? result.data
    : result;

  if (data === null || data === undefined) {
    throw new Error("api.json does not contain usable data.");
  }

  return data;
};

/**
 * Load the latest public/api.json file.
 */
const fetchLocalJson = async ({ signal } = {}) => {
  const controller = new AbortController();
  let didTimeout = false;

  const handleExternalAbort = () => {
    controller.abort(signal?.reason);
  };

  if (signal) {
    if (signal.aborted) {
      controller.abort(signal.reason);
    } else {
      signal.addEventListener("abort", handleExternalAbort, {
        once: true,
      });
    }
  }

  const timeoutId = window.setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  try {
    /*
     * The timestamp guarantees that every request uses
     * the newest api.json instead of a browser/CDN cache.
     */
    const requestUrl = `${API_JSON_URL}?v=${Date.now()}`;

    const response = await fetch(requestUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(
        `Unable to load api.json: ${response.status} ${response.statusText}`,
      );
    }

    try {
      return await response.json();
    } catch {
      throw new Error(
        'The file "public/api.json" does not contain valid JSON.',
      );
    }
  } catch (error) {
    if (didTimeout) {
      const timeoutError = new Error(
        `Loading api.json timed out after ${REQUEST_TIMEOUT_MS}ms.`,
      );

      timeoutError.name = "TimeoutError";
      throw timeoutError;
    }

    throw error;
  } finally {
    window.clearTimeout(timeoutId);

    signal?.removeEventListener?.("abort", handleExternalAbort);
  }
};

/**
 * Fetch the newest api.json data.
 *
 * No localStorage or sessionStorage caching is used.
 */
export const fetchAndStoreData = async ({ signal, force = false } = {}) => {
  if (!isBrowser()) return null;

  /*
   * Reuse only a currently running request.
   * Once completed, the next call fetches api.json again.
   */
  if (activeRequest && !force) {
    return activeRequest;
  }

  activeRequest = (async () => {
    const result = await fetchLocalJson({ signal });
    const data = extractData(result);

    if (signal?.aborted) {
      throw signal.reason || new DOMException("Aborted", "AbortError");
    }

    /*
     * This is only temporary JavaScript memory.
     * It disappears when the page reloads or the user logs out.
     */
    currentData = data;

    return data;
  })();

  try {
    return await activeRequest;
  } finally {
    activeRequest = null;
  }
};

/**
 * Return the latest data already loaded during the current page session.
 *
 * This does not read from localStorage.
 */
export const getStoredData = () => currentData;

/**
 * Cache metadata is no longer used.
 */
export const getStoredDataMeta = () => null;

/**
 * Clear temporary in-memory data.
 */
export const clearStoredData = () => {
  currentData = null;
  activeRequest = null;
};

/**
 * Load api.json during application startup.
 */
const initializeApiData = () => {
  fetchAndStoreData({ force: true }).catch((error) => {
    if (isAbortError(error)) {
      console.warn("api.json request was cancelled.");
      return;
    }

    console.error("Unable to initialize api.json data:", error);
  });
};

if (isBrowser()) {
  const normalizedPath =
    window.location.pathname.replace(/\/+$/, "").toLowerCase() || "/";

  const publicAuthPages = ["/login", "/forgot-password", "/reset-password"];

  const isPublicAuthPage = publicAuthPages.includes(normalizedPath);

  if (!isPublicAuthPage) {
    if (document.readyState === "loading") {
      window.addEventListener("DOMContentLoaded", initializeApiData, {
        once: true,
      });
    } else {
      initializeApiData();
    }
  }
}
